import { useCallback, useRef } from 'react';

import { MIN_CURSOR_MOVE_THRESHOLD_PX } from 'constants/params';
import { StoryBoardTracks } from 'services/storyboard.service';
import { RootState, store } from 'store/store';
import { isNull } from 'components/app/_helpers/_app.helper';
import { useClipThumbPrevContext } from 'components/app/_mediator/clip-thumb-prev.provider';
import { useAudioTrackContext } from 'components/app/audio-track/_mediator/audio-track.provider';
import { useWsSeek } from 'components/app/audio-track/_mediator/use-ws-seek';
import { useSbCb } from 'components/app/storyboard/_mediator/use-sb-cb';
import { useFinalcutCb } from 'components/app/finalcuts/_mediator/use-finalcut-cb';
import { OnZoomProps } from './zoom.provider';

export type ZoomElementType = 'ws' | StoryBoardTracks | 'finalcuts';

export const useZoomSeek = () => {
  const cursorXPosOnLastZoom = useRef<number | null>(null);
  const { hoveredClip } = useClipThumbPrevContext();
  const { ws, wsGetCursorFraction, leftOffset } = useAudioTrackContext();
  const { sbSelectClip } = useSbCb();
  const { fcSelectClip } = useFinalcutCb();
  const seekTo = useWsSeek();

  const actionSbSelectClip = (trackID: StoryBoardTracks) => {
    const { storyBoard, activeClip } = selectData(store.getState());
    const clipId = hoveredClip.current;
    if (!clipId) return;
    if (activeClip === clipId) return;
    sbSelectClip(clipId, storyBoard[trackID].indexOf(clipId), trackID);
  };

  const actionFcSelectClip = () => {
    const { fcStoryBoard, fcActiveClip } = selectData(store.getState());
    const clipId = hoveredClip.current;
    if (!clipId) return;
    if (clipId === fcActiveClip) return;
    fcSelectClip(fcStoryBoard.indexOf(clipId as CellClip));
  };

  const resetXPos = (listenWheel: boolean) => {
    if (!listenWheel) cursorXPosOnLastZoom.current = null;
  };

  const zoomSeek = useCallback(({ event, zoomElementType }: OnZoomProps) => {
    if (!zoomElementType || !ws.current) return;

    const xPos = event.clientX - leftOffset.current;
    const isMouseMovementSlight =
      !isNull(cursorXPosOnLastZoom.current) &&
      Math.abs(cursorXPosOnLastZoom.current - xPos) < MIN_CURSOR_MOVE_THRESHOLD_PX;

    cursorXPosOnLastZoom.current = xPos;

    if (isMouseMovementSlight) return;
    if (zoomElementType === 'ws') seekTo(wsGetCursorFraction(event.clientX));
    else if (zoomElementType === 'finalcuts') actionFcSelectClip();
    else actionSbSelectClip(zoomElementType as StoryBoardTracks);
  }, []);

  return { zoomSeek, resetXPos };
};

/* S E L E C T O R */

const selectData = (state: RootState) => {
  const { activeClip, storyBoard } = state.editor;
  const { fcStoryBoard } = state.fc__data;
  const { fcActiveClip } = state.fc__controls;
  return { storyBoard, fcStoryBoard, activeClip, fcActiveClip };
};
